import type { Document as RichTextDocument } from '@contentful/rich-text-types';

import type { CTAContent } from '@/components/Elements/CTA';
import type { IconName } from '@/components/Icon/Icon';
import type { ButtonContent } from '@/components/OldButton/OldButton';
import type { SectionType } from '@/components/Section/Section';
import type {
  BlogCategoryCf,
  BlogCategoryNavCf,
  DynamicRedirectPageCF,
  ExitIntentCF,
  GlobalFooterCf,
  GlobalNavCf,
  SectionCf,
} from '@/types';

import type {
  BlogCategoryPageDynamicContent,
  BlogCategoryPageLinkedArticlesContentCF,
  BlogPageLinkedArticlesContentCF,
  SunrunShopProductPageEnhancedCF,
} from './api/api.types';
export interface AnyContent extends Contentful {
  fields: {
    [key: string]: unknown;
  };
}

export interface CardContent extends Contentful {
  fields: {
    cta: CTAContent;
    image: FrontifyImageContent[];
    title: string;
    subtitle: string;
    description: string;
    richDescription?: RichTextDocument;
    cardImageAltText?: string;
  };
}

export interface CardGroupContent extends Contentful {
  fields: {
    cards: CardContent[];
    title: string;
  };
}

export interface ComponentSys {
  space: SubSys;
  type: string;
  id: string;
  contentType: SubSys;
  revision: number;
  createdAt: Date;
  updatedAt: Date;
  publishedAt?: Date;
  firstPublishedAt?: Date;
  publishedVersion?: number;
  environment: SubSys;
  locale: string;
}

interface SubSys {
  sys: {
    id: string;
    linkType: string;
    type: string;
  };
}

// CONTENT MODELS

export interface Footer extends Contentful {
  fields: {
    categories: Menu[];
    cta: CTAContent;
    legalLinks: Menu;
    name: string;
    socialLinks: SocialMediaLink[];
    topText: string;
    copyrightText: string;
    textColor?: string;
  };
}

export interface FrontifyImageContent {
  created: string;
  ext: string;
  generic_url: string;
  height: number;
  id: number;
  preview_url: string;
  src: string;
  title: string;
  width: number;
  name?: string;
}

export enum HeaderVariant {
  'Light Overlay' = 'Light Overlay',
  'Dark Overlay' = 'Dark Overlay',
  'Primary Blue' = 'Primary Blue',
}

export interface HeaderContent extends Contentful {
  fields: {
    categories: Menu[];
    cta: CTAContent | ButtonContent;
    cta2: CTAContent;
    hamburger?: Menu;
    hamburgerCta?: CTAContent;
    mySunrunButton?: CTAContent;
    name: string;
    promoBanner?: RichText;
    telephone?: CTAContent;
    appearance: HeaderVariant;
    socialMediaLinks?: SocialMediaLink[];
  };
}

export interface GlobalNavContent extends Contentful {
  fields: {
    categories: DropdownMenu[];
    promoCta?: CTAContent | PromoStateConstraintsList;
    loginCta: CTAContent;
    primaryCta: CTAContent;
    secondaryCta: CTAContent;
    name: string;
    style: ContentStyle;
    immersive: boolean;
  };
}

export enum ContentStyle {
  OnDark = 'OnDark',
  OnLight = 'OnLight',
}

export enum TextAlignment {
  Left = 'Left',
  Center = 'Center',
  Right = 'Right',
}

interface DropdownMenuItem extends Contentful {
  fields: FieldlessDropdownMenuItem;
}

export interface FieldlessDropdownMenuItem {
  pageTitle: string;
  pageLink: string;
  chipText?: string;
  subtext?: string;
}

export interface FieldlessDropdownMenu {
  links: FieldlessDropdownMenuItem[];
  name: string;
  featured: FieldlessFeatureCard;
}

interface DropdownMenu extends Contentful {
  fields: {
    links: DropdownMenuItem[];
    name: string;
    featured: FeatureCard;
  };
}

interface FeatureCard extends Contentful {
  fields: FieldlessFeatureCard;
}

interface FieldlessFeatureCard {
  image: FrontifyImageContent[];
  cardTitle: string;
  link: string;
  buttonLabel: string;
  style: ContentStyle;
}

// Old navigation
interface Menu extends Contentful {
  fields: {
    links: CTAContent[];
    name: string;
  };
}

interface RichText {
  content: unknown[];
  data: unknown;
  nodeType: string;
}

export interface Seo {
  fields: {
    enableSearchEngineIndexing?: boolean;
    metaText?: string;
    metaTitle?: string;
    metaKeywords?: string[];
    previewImage?: FrontifyImageContent[];
    canonicalUrl?: string;
    excludeFromSitemap?: boolean;
  };
}

export enum SocialSites {
  FACEBOOK = 'Facebook',
  LINKEDIN = 'LinkedIn',
  TWITTER = 'Twitter',
}

export interface SocialMediaLink extends Contentful {
  fields: {
    icon?: FrontifyImageContent[];
    url: string;
    title: string;
  };
}

enum VerticalSpacing {
  Top = 'Top',
  Center = 'Center',
  Bottom = 'Bottom',
  Even = 'Even',
}

interface ColumnContent extends Contentful {
  fields: {
    content: AnyContent[];
    verticalSpacing: VerticalSpacing;
  };
}

export enum Site {
  CMS_SUNRUN_COM = 'cms.sunrun.com',
  CMS2_SUNRUN_COM = 'cms2.sunrun.com',
}

export interface Contentful {
  sys: ComponentSys;
}

export interface LinkContent extends Contentful {
  fields: {
    text: string;
    url: string;
    videoUrl?: string;
  };
}

export interface IconNameContent extends Contentful {
  fields: {
    name: IconName;
  };
}

interface StateConstraintsList extends Contentful {
  fields: {
    constraintName: string;

    // biome-ignore lint/suspicious/noExplicitAny: Automated migration to Biome
    default?: any;
    constraints: StateConstraint[];
  };
}

export interface StateConstraint extends Contentful {
  fields: {
    constraintStateName: string;
    zipCodeList?: string;
    constraintType: boolean;
    states?: string[];

    // biome-ignore lint/suspicious/noExplicitAny: Automated migration to Biome
    alternateContent?: any;
  };
}

export interface PromoStateConstraint extends StateConstraint {
  fields: {
    constraintStateName: string;
    zipCodeList?: string;
    constraintType: boolean;
    states?: string[];
    alternateContent?: CTAContent;
  };
}

export interface PromoStateConstraintsList extends StateConstraintsList {
  fields: {
    constraintName: string;
    zipCodeList?: string;
    default?: CTAContent;
    constraints: PromoStateConstraint[];
  };
}

interface SectionStateConstraint extends StateConstraint {
  fields: {
    constraintStateName: string;
    zipCodeList?: string;
    constraintType: boolean;
    states?: string[];
    alternateContent: ColumnContent[];
  };
}

export interface SectionStateConstraintsList extends StateConstraintsList {
  fields: {
    constraintName: string;
    zipCodeList?: string;
    default: ColumnContent[];
    constraints: SectionStateConstraint[];
  };
}

export type FeaturedComponent<T> = T & { isFeatured?: boolean };

export enum PageContentType {
  STANDARD = 'standardPage',
  BLOG = 'blogPage',
  BLOG_CATEGORY = 'blogCategoryPage',
  DYNAMIC_REDIRECT = 'dynamicRedirectPage',
  SUNRUN_SHOP_PRODUCT = 'ecommStoreProductPage',
}
export interface FooterMenuContent extends Contentful {
  fields: {
    name?: string;
    links: LinkContent[];
  };
}

interface SocialIconLinkContent extends Contentful {
  fields: {
    url: string;
    icon: IconNameContent;
  };
}

// section issue
export interface GlobalFooterContent extends Contentful {
  fields: {
    style: ContentStyle;
    t1Menu: FooterMenuContent;
    linkColumns: FooterMenuContent[];
    solarByStateMenu: FooterMenuContent;
    socialIconLinks: SocialIconLinkContent[];
    copyrightText: string;
    legalLinks: FooterMenuContent;
  };
}

export interface StandardPageContent extends Contentful {
  fields: {
    header: HeaderContent | GlobalNavContent;
    sections: SectionType[];
    footer: Footer | GlobalFooterContent;
  };
}

export interface BlogPageContent extends Contentful {
  fields: {
    path: string;
    body: RichTextDocument;
    blogTitle: string;
    blogSubtitle?: string;
    backButton?: LinkContent;
    heroImage: [FrontifyImageContent];
    heroImageAltText?: string;
    video?: string;
    author?: string;
    publicationDate: string;
    lastUpdated?: string;
    header: HeaderContent | GlobalNavContent;
    footer: Footer | GlobalFooterContent;
    sections: SectionType[];
    socialShare: SocialSites[];
    tags: string[];
    showTableOfContents: boolean;
    curatedArticlesListHeading?: string;
    linkedArticleContent?: BlogPageLinkedArticlesContentCF;
  };
}

export interface BlogCategoryPageContentType extends Contentful {
  fields: {
    contentTitle?: string;
    header?: GlobalNavCf;
    category?: BlogCategoryCf;
    categoryNav?: BlogCategoryNavCf;
    eyebrow?: string;
    heading?: string;
    subheading?: string;
    sections?: SectionCf[];
    footer?: GlobalFooterCf;
    linkedArticleContent?: BlogCategoryPageLinkedArticlesContentCF;
    dynamicBlogContent?: BlogCategoryPageDynamicContent;
  };
}

export type PageContentTypes =
  | StandardPageContent
  | BlogPageContent
  | BlogCategoryPageContentType
  | DynamicRedirectPageCF
  | SunrunShopProductPageEnhancedCF;

export interface PageContent extends Contentful {
  fields: {
    path: string;
    site: Site;
    seoFields: Seo;
    fullStoryEnabled: boolean;
    exitIntent?: ExitIntentCF;
    pageContent: PageContentTypes;
  };
}

interface GenericPageType<T extends PageContentTypes> {
  fields: {
    pageContent: T;
  };
}

export type StandardPageType = GenericPageType<StandardPageContent>;
